export default function SystemArchitecture() {
  const layers = [
    {
      name: '前端应用层',
      color: 'from-blue-500 to-cyan-500',
      items: [
        { icon: 'ri-user-smile-line', title: '学生实训端', desc: '分学段UI' },
        { icon: 'ri-user-star-line', title: '教师智教端', desc: '教学管理' },
        { icon: 'ri-admin-line', title: '校级管理后台', desc: '数据统计' }
      ]
    },
    {
      name: '中台服务层',
      color: 'from-purple-500 to-pink-500',
      items: [
        { icon: 'ri-book-open-line', title: '课程资源中心', desc: '标准课件库' },
        { icon: 'ri-file-list-3-line', title: '作业/评价引擎', desc: '智能批改' },
        { icon: 'ri-cloud-line', title: 'AI调度网关', desc: 'API Gateway' }
      ]
    },
    {
      name: '底层能力层',
      color: 'from-orange-500 to-red-500',
      items: [
        { icon: 'ri-brain-line', title: '大模型接入', desc: 'LLM集成' },
        { icon: 'ri-shield-check-line', title: '内容安全护栏', desc: 'Guardrails' },
        { icon: 'ri-lock-line', title: '数据隐私沙箱', desc: 'PII脱敏' }
      ]
    }
  ];

  const securityFeatures = [
    {
      icon: 'ri-shield-star-line',
      title: '内容安全卫士',
      features: ['敏感词库拦截', '输出内容审核', '伦理引导弹窗']
    },
    {
      icon: 'ri-user-forbid-line',
      title: '隐私脱敏层',
      features: ['PII自动识别', '敏感信息替换', '实名认证分级']
    },
    {
      icon: 'ri-database-2-line',
      title: '校本特色配置',
      features: ['知识库上传', '特色课程定制', '因地制宜开发']
    }
  ];

  return (
    <section id="architecture" className="py-24 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
            <i className="ri-stack-line"></i>
            <span className="text-sm font-semibold">SYSTEM ARCHITECTURE</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            四大核心子系统
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            分层架构设计，确保系统稳定性、安全性与可扩展性
          </p>
        </div>

        {/* Architecture Layers */}
        <div className="space-y-6 mb-20">
          {layers.map((layer, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-center space-x-4 mb-6">
                <div className={`px-6 py-2 bg-gradient-to-r ${layer.color} text-white rounded-full font-bold text-lg whitespace-nowrap`}>
                  {layer.name}
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-gray-300 to-transparent"></div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {layer.items.map((item, idx) => (
                  <div 
                    key={idx}
                    className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${layer.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <i className={`${item.icon} text-2xl text-white`}></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Security & Compliance */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-12 border-2 border-red-200">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full mb-4">
              <i className="ri-shield-check-line"></i>
              <span className="text-sm font-semibold">SECURITY & COMPLIANCE</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              管理与安全合规端
            </h3>
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">
              严格保护学生个人信息，确保技术应用安全规范
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h4>
                <ul className="space-y-3">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3 text-gray-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 border-2 border-red-200">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h5 className="font-bold text-gray-900 mb-2 text-lg">政策合规保障</h5>
                <p className="text-gray-700 leading-relaxed">
                  严格遵循《浙江省中小学人工智能通识教育指导意见》要求，建立多层次安全防护体系。所有学生数据经过PII脱敏处理，AI生成内容经过双重审核，确保教育环境绿色健康。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Development Roadmap */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">开发优先级路线图</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-2xl p-8 border-2 border-blue-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  P0
                </div>
                <div>
                  <div className="font-bold text-gray-900">基建期</div>
                  <div className="text-sm text-gray-600">1个月</div>
                </div>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span>账号权限体系</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span>大模型接口封装</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <span>安全拦截模块</span>
                </li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-2xl p-8 border-2 border-purple-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  P1
                </div>
                <div>
                  <div className="font-bold text-gray-900">核心功能期</div>
                  <div className="text-sm text-gray-600">2个月</div>
                </div>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span>文生图&对话助手</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span>智能体搭建工坊</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-purple-600 mt-1"></i>
                  <span>课堂控制功能</span>
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 rounded-2xl p-8 border-2 border-orange-200">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                  P2
                </div>
                <div>
                  <div className="font-bold text-gray-900">深度功能期</div>
                  <div className="text-sm text-gray-600">3个月</div>
                </div>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span>Python编程环境</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span>神经网络可视化</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-checkbox-circle-fill text-orange-600 mt-1"></i>
                  <span>素养评价报告</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
